import { Location } from '@angular/common';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '@netfoundry-ui/shared/services';
import { firstValueFrom, Subscription } from 'rxjs';
import { SERVICE_POLICY_EXTENSION_SERVICE } from 'ziti-console-lib';
import { ServicePoliciesExtensionService } from '../service-policies-extension/service-policies-extension.service';

const POLICY_CONFLICT_DETAILS_PAGE_SIZE = 999;
const POLICY_CONFLICT_DETAILS_SORT = 'name,asc';

@Component({
    selector: 'netfoundry-ui-service-policy-conflict-details-page',
    templateUrl: './service-policy-conflict-details-page.component.html',
    styleUrls: ['./service-policy-conflict-details-page.component.scss'],
    standalone: false,
})
export class ServicePolicyConflictDetailsPageComponent {
    private subscription = new Subscription();
    private intersectionObserver?: IntersectionObserver;
    private loadMoreTriggerElement?: ElementRef<HTMLDivElement>;

    isLoading = true;
    isLoadingMore = false;
    currentPage = -1;
    totalElements = 0;
    totalPages = 0;

    @ViewChild('loadMoreTrigger')
    set loadMoreTrigger(element: ElementRef<HTMLDivElement> | undefined) {
        this.loadMoreTriggerElement = element;
        this.refreshLoadMoreObserver();
    }

    constructor(
        @Inject(SERVICE_POLICY_EXTENSION_SERVICE) public svc: ServicePoliciesExtensionService,
        private apiService: ApiService,
        private location: Location,
        private router: Router,
        private route: ActivatedRoute,
    ) {
    }

    get pageData() {
        const existingPageData = this.svc.conflictPageData || {};
        const conflictDetails = this.svc.conflictDetails || existingPageData.conflictDetails || {};

        return {
            title: 'Potential Conflicts',
            subtitle: 'This policy may have potential conflicts. Please review before proceeding.',
            ...existingPageData,
            conflictDetails,
            identityRoles: this.getIdentityAttributes(conflictDetails),
            serviceRoles: this.getServiceAttributes(conflictDetails),
            policyType: conflictDetails?.policyType || existingPageData.policyType,
            pagination: {
                page: this.currentPage,
                size: conflictDetails?.page?.size || POLICY_CONFLICT_DETAILS_PAGE_SIZE,
                sort: existingPageData?.pagination?.sort || POLICY_CONFLICT_DETAILS_SORT,
                totalElements: this.totalElements,
                totalPages: this.totalPages,
                hasMore: this.hasMoreIdentities,
            },
            emptyStateMessage: 'This policy may have potential conflicts, but detailed information is not available at this time.',
        };
    }

    get hasMoreIdentities() {
        if (this.totalPages > 0) {
            return this.currentPage + 1 < this.totalPages;
        }

        return this.totalElements > 0 && this.loadedIdentityCount < this.totalElements;
    }

    get loadedIdentityCount() {
        return Array.isArray(this.svc.conflictDetails?.identities) ? this.svc.conflictDetails.identities.length : 0;
    }

    headerActionRequested(action: any) {
        if (action?.name === 'close') {
            this.goBack();
        }
    }

    ngOnInit() {
        this.subscription.add(
            this.apiService.currentNetwork.subscribe((network: any) => {
                const servicePolicyId = this.route.snapshot.paramMap.get('id') || '';
                if (!network?.id || !servicePolicyId) {
                    return;
                }

                this.loadConflictDetailsPage(network.id, servicePolicyId, 0);
            })
        );
    }

    loadMore() {
        const servicePolicyId = this.route.snapshot.paramMap.get('id') || '';
        if (!this.hasMoreIdentities || this.isLoading || this.isLoadingMore || !this.svc.currentNetwork?.id || !servicePolicyId) {
            return;
        }

        this.loadConflictDetailsPage(this.svc.currentNetwork.id, servicePolicyId, this.currentPage + 1, true);
    }

    goBack() {
        if (history.state?.fromConflictSourceForm) {
            this.location.back();
            return;
        }

        this.router.navigate(['../'], { relativeTo: this.route, replaceUrl: true });
    }

    ngOnDestroy() {
        this.intersectionObserver?.disconnect();
        this.subscription.unsubscribe();
    }

    private loadConflictDetailsPage(networkId: string, servicePolicyId: string, page: number, append = false) {
        if (append) {
            this.isLoadingMore = true;
        } else {
            this.isLoading = true;
            this.currentPage = -1;
            this.totalElements = 0;
            this.totalPages = 0;
        }

        firstValueFrom(
            this.svc.getPolicyConflictDetails(
                networkId,
                servicePolicyId,
                [],
                [],
                'AnyOf',
                'Bind',
                page,
                POLICY_CONFLICT_DETAILS_PAGE_SIZE,
                POLICY_CONFLICT_DETAILS_SORT
            )
        )
            .then((conflictDetails: any) => {
                const incomingConflictDetails = conflictDetails || {};
                const mergedConflictDetails = append
                    ? this.mergeConflictDetails(this.svc.conflictDetails, incomingConflictDetails)
                    : incomingConflictDetails;

                this.svc.conflictDetails = mergedConflictDetails;
                this.currentPage = this.getPageNumber(incomingConflictDetails, page);
                this.totalElements = this.getTotalElements(incomingConflictDetails, mergedConflictDetails);
                this.totalPages = this.getTotalPages(incomingConflictDetails, this.totalElements);
                this.svc.conflictPageData = {
                    ...(this.svc.conflictPageData || {}),
                    title: 'Potential Conflicts',
                    subtitle: 'This policy may have potential conflicts. Please review before proceeding.',
                    conflictDetails: mergedConflictDetails,
                    identityRoles: this.getIdentityAttributes(mergedConflictDetails),
                    serviceRoles: this.getServiceAttributes(mergedConflictDetails),
                    policyType: mergedConflictDetails?.policyType || this.svc.conflictPageData?.policyType,
                    pagination: {
                        page: this.currentPage,
                        size: incomingConflictDetails?.page?.size || POLICY_CONFLICT_DETAILS_PAGE_SIZE,
                        sort: POLICY_CONFLICT_DETAILS_SORT,
                        totalElements: this.totalElements,
                        totalPages: this.totalPages,
                        hasMore: this.hasMoreIdentities,
                    },
                    emptyStateMessage: 'This policy may have potential conflicts, but detailed information is not available at this time.',
                };
            })
            .catch(() => {
                if (append) {
                    return;
                }

                this.svc.conflictDetails = {};
                this.svc.conflictPageData = {
                    ...(this.svc.conflictPageData || {}),
                    title: 'Potential Conflicts',
                    subtitle: 'This policy may have potential conflicts. Please review before proceeding.',
                    conflictDetails: {},
                    identityRoles: [],
                    serviceRoles: [],
                    pagination: {
                        page: -1,
                        size: POLICY_CONFLICT_DETAILS_PAGE_SIZE,
                        sort: POLICY_CONFLICT_DETAILS_SORT,
                        totalElements: 0,
                        totalPages: 0,
                        hasMore: false,
                    },
                    emptyStateMessage: 'This policy may have potential conflicts, but detailed information is not available at this time.',
                };
            })
            .finally(() => {
                this.isLoading = false;
                this.isLoadingMore = false;
                this.refreshLoadMoreObserver();
            });
    }

    private mergeConflictDetails(existingDetails: any, incomingDetails: any) {
        const existingIdentities = Array.isArray(existingDetails?.identities) ? existingDetails.identities : [];
        const incomingIdentities = Array.isArray(incomingDetails?.identities) ? incomingDetails.identities : [];
        const identityMap = new Map<string, any>();

        [...existingIdentities, ...incomingIdentities].forEach((identityEntry: any, index: number) => {
            const identityKey = identityEntry?.identity?.zitiId || identityEntry?.identity?.name || `${index}`;
            identityMap.set(identityKey, identityEntry);
        });

        return {
            ...(existingDetails || {}),
            ...(incomingDetails || {}),
            identities: Array.from(identityMap.values()),
        };
    }

    private getIdentityAttributes(conflictDetails: any) {
        return conflictDetails?.identityAttributes || conflictDetails?.identityAttribute || [];
    }

    private getServiceAttributes(conflictDetails: any) {
        return conflictDetails?.serviceAttributes || conflictDetails?.serviceAttribute || [];
    }

    private getPageNumber(conflictDetails: any, fallbackPage: number) {
        const pageNumber = Number(conflictDetails?.page?.number);
        return Number.isFinite(pageNumber) ? pageNumber : fallbackPage;
    }

    private getTotalElements(conflictDetails: any, mergedConflictDetails: any) {
        const totalElements = Number(conflictDetails?.page?.totalElements);
        if (Number.isFinite(totalElements)) {
            return totalElements;
        }

        return Array.isArray(mergedConflictDetails?.identities) ? mergedConflictDetails.identities.length : 0;
    }

    private getTotalPages(conflictDetails: any, totalElements: number) {
        const totalPages = Number(conflictDetails?.page?.totalPages);
        if (Number.isFinite(totalPages)) {
            return totalPages;
        }

        const pageSize = Number(conflictDetails?.page?.size) || POLICY_CONFLICT_DETAILS_PAGE_SIZE;
        return totalElements > 0 ? Math.ceil(totalElements / pageSize) : 0;
    }

    private refreshLoadMoreObserver() {
        this.intersectionObserver?.disconnect();

        if (!this.loadMoreTriggerElement || !this.hasMoreIdentities || typeof IntersectionObserver === 'undefined') {
            return;
        }

        this.intersectionObserver = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                this.loadMore();
            }
        }, {
            rootMargin: '0px 0px 240px 0px',
        });

        this.intersectionObserver.observe(this.loadMoreTriggerElement.nativeElement);
    }
}
